/** 根地址导航只持久化入口、身份和历史代次，不保存业务页面数据。 */
export const ROOT_PATH = '/'
export const ROOT_NAVIGATION_HISTORY_KEY = 'rootNavigation'
export const ROOT_NAVIGATION_HISTORY_VERSION = 1
export const ROOT_NAVIGATION_SCOPE_KEY = 'navigation.root.scope'
export const ROOT_NAVIGATION_IDENTITY_KEY = 'navigation.root.identity'
export const ROOT_NAVIGATION_BOOTSTRAP_KEY = 'navigation.root.bootstrap'
export const ROOT_NAVIGATION_PENDING_KEY = 'navigation.root.pending'

const contexts = new WeakMap()
const navigationKeys = [ROOT_NAVIGATION_SCOPE_KEY, ROOT_NAVIGATION_IDENTITY_KEY, ROOT_NAVIGATION_BOOTSTRAP_KEY, ROOT_NAVIGATION_PENDING_KEY]
const nonBusinessPaths = new Set(['/', '/intro', '/login', '/resources', '/password-change-required'])

function storageOf(browserWindow) {
  try { return browserWindow?.sessionStorage } catch { return null }
}

function read(storage, key) {
  try { return storage?.getItem(key) || '' } catch { return '' }
}

function write(storage, key, value) {
  try {
    if (value) storage?.setItem(key, value)
    else storage?.removeItem(key)
  } catch { /* 禁用存储时仍可使用当前文档和 history.state。 */ }
}

function parse(value) {
  try { return JSON.parse(value) } catch { return null }
}

function newScope(browserWindow) {
  return browserWindow?.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

function contextOf(browserWindow) {
  let context = contexts.get(browserWindow)
  if (!context) {
    const storage = storageOf(browserWindow)
    const historyContext = browserWindow.history?.state?.[ROOT_NAVIGATION_HISTORY_KEY]
    const storedScope = read(storage, ROOT_NAVIGATION_SCOPE_KEY)
    const validHistoryScope = historyContext?.version === ROOT_NAVIGATION_HISTORY_VERSION
      && typeof historyContext.scope === 'string' && historyContext.scope
    context = {
      scope: storedScope || validHistoryScope || newScope(browserWindow),
      identity: read(storage, ROOT_NAVIGATION_IDENTITY_KEY)
        || (!storedScope && validHistoryScope && typeof historyContext.identity === 'string' && historyContext.identity)
        || '',
      pending: parse(read(storage, ROOT_NAVIGATION_PENDING_KEY)),
    }
    contexts.set(browserWindow, context)
    write(storage, ROOT_NAVIGATION_SCOPE_KEY, context.scope)
  }
  return context
}

export function isInternalFullPath(value) {
  return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')
    && !/[\\\u0000-\u001f\u007f]/.test(value)
}

function businessTarget(value) {
  return isInternalFullPath(value) && !nonBusinessPaths.has(value.split(/[?#]/, 1)[0]) ? value : ''
}

export function rootNavigationIdentity(profile) {
  return profile?.userId ? JSON.stringify([String(profile.userId), String(profile.companyIdFk || '')]) : ''
}

export function getRootNavigationScope(browserWindow = window) {
  return contextOf(browserWindow).scope
}

export function getRootNavigationIdentity(browserWindow = window) {
  return contextOf(browserWindow).identity
}

export function savePendingRootTarget(target, browserWindow = window) {
  const fullPath = businessTarget(target)
  if (!fullPath) return
  const context = contextOf(browserWindow)
  context.pending = { fullPath, scope: context.scope, identity: context.identity }
  write(storageOf(browserWindow), ROOT_NAVIGATION_PENDING_KEY, JSON.stringify(context.pending))
}

export function clearRootNavigation(browserWindow = window, { preservePending = false } = {}) {
  const storage = storageOf(browserWindow)
  const pending = preservePending ? parse(read(storage, ROOT_NAVIGATION_PENDING_KEY)) || contextOf(browserWindow).pending : null
  const context = { scope: newScope(browserWindow), identity: '', pending: null }
  contexts.set(browserWindow, context)
  navigationKeys.forEach((key) => write(storage, key, ''))
  write(storage, ROOT_NAVIGATION_SCOPE_KEY, context.scope)
  if (pending && businessTarget(pending.fullPath)) {
    context.pending = { ...pending, scope: context.scope }
    write(storage, ROOT_NAVIGATION_PENDING_KEY, JSON.stringify(context.pending))
  }
}

export function setRootNavigationIdentity(profile, browserWindow = window) {
  const identity = rootNavigationIdentity(profile)
  const previous = contextOf(browserWindow)
  if (previous.identity && previous.identity !== identity) clearRootNavigation(browserWindow)
  const context = contextOf(browserWindow)
  context.identity = identity
  write(storageOf(browserWindow), ROOT_NAVIGATION_IDENTITY_KEY, identity)
}

export function consumePendingRootTarget(profile, browserWindow = window) {
  const storage = storageOf(browserWindow)
  const context = contextOf(browserWindow)
  const pending = parse(read(storage, ROOT_NAVIGATION_PENDING_KEY)) || context.pending
  write(storage, ROOT_NAVIGATION_PENDING_KEY, '')
  context.pending = null
  return pending && pending.scope === context.scope
    && (!pending.identity || pending.identity === rootNavigationIdentity(profile))
    ? businessTarget(pending.fullPath) : ''
}

/** 优先导入旧地址和新窗口入口；history.state 是同标签页刷新的唯一页面真值。 */
export function resolveRootNavigationStart(browserWindow = window) {
  const storage = storageOf(browserWindow)
  const context = contextOf(browserWindow)
  const { pathname = '/', search = '', hash = '' } = browserWindow.location
  const bootstrap = parse(read(storage, ROOT_NAVIGATION_BOOTSTRAP_KEY))
  write(storage, ROOT_NAVIGATION_BOOTSTRAP_KEY, '')
  const legacy = pathname !== '/' ? `${pathname}${search}${hash}` : ''
  let target = isInternalFullPath(legacy) ? legacy
    : bootstrap && bootstrap.scope === context.scope && isInternalFullPath(bootstrap.fullPath)
      ? bootstrap.fullPath : ''
  if (target) {
    if (target.split(/[?#]/, 1)[0] === '/login') {
      const redirect = new URLSearchParams(search).get('redirect')
      if (redirect) savePendingRootTarget(redirect, browserWindow)
    } else savePendingRootTarget(target, browserWindow)
    return target
  }
  const state = browserWindow.history?.state
  const marker = state?.[ROOT_NAVIGATION_HISTORY_KEY]
  if (marker?.version === ROOT_NAVIGATION_HISTORY_VERSION && marker.scope === context.scope
    && marker.identity === context.identity && Number.isInteger(state.position) && state.position >= 0
    && isInternalFullPath(state.current)) {
    return state.current
  }
  return '/intro'
}

/** 同步写入新窗口独立存储；失败交给调用方关闭空白窗口并提示。 */
export function setRootWindowStartup(target, childWindow) {
  if (!isInternalFullPath(target)) throw new Error('无效的内部页面地址')
  const storage = childWindow.sessionStorage
  const identity = storage.getItem(ROOT_NAVIGATION_IDENTITY_KEY) || ''
  navigationKeys.forEach((key) => storage.removeItem(key))
  const scope = newScope(childWindow)
  storage.setItem(ROOT_NAVIGATION_SCOPE_KEY, scope)
  if (identity) storage.setItem(ROOT_NAVIGATION_IDENTITY_KEY, identity)
  storage.setItem(ROOT_NAVIGATION_BOOTSTRAP_KEY, JSON.stringify({ fullPath: target, scope }))
  contexts.delete(childWindow)
}

/** 整页退出仍重载文档，但其下一次入口由历史状态指定，URL 始终为根。 */
export function prepareRootNavigationReload(target, browserWindow = window) {
  const fullPath = isInternalFullPath(target) ? target : '/login'
  const context = contextOf(browserWindow)
  const previous = browserWindow.history?.state || {}
  const state = {
    ...previous, back: null, current: fullPath, forward: null, replaced: true,
    position: Number.isInteger(previous.position) && previous.position >= 0 ? previous.position : Math.max(0, browserWindow.history.length - 1),
    scroll: null,
    [ROOT_NAVIGATION_HISTORY_KEY]: { version: ROOT_NAVIGATION_HISTORY_VERSION, scope: context.scope, identity: context.identity },
  }
  try { browserWindow.history.replaceState(state, '', ROOT_PATH) } catch {
    write(storageOf(browserWindow), ROOT_NAVIGATION_BOOTSTRAP_KEY, JSON.stringify({ fullPath, scope: context.scope }))
  }
}
